import { useMemo } from "react";
import { useQuery } from "react-query";
import { getFloorAvailability } from "../../../utils/axios-requests";
import { useRemoteFetchFloorPlan } from "../../../hooks/Remote/FloorPlan/useRemoteFetchFloorPlan";
import type { BookingScheduleInterface } from "../../../features/Booking-Form/typings/booking.types";
import type { FloorAvailability } from "../../../features/FloorManager/typings/floor-inventory.entity";
import {
  buildChangeRoomFloorAvailabilityArgs,
  resolveFloorInventoryIdFromSchedule
} from "./change-conference-room.functions";

export function useChangeConferenceRoomFloorData(
  schedule: BookingScheduleInterface,
  enabled: boolean,
  selectedFloorInventoryId?: number
) {
  const scheduleFloorInventoryId = resolveFloorInventoryIdFromSchedule(schedule) ?? 0;
  const floorInventoryId = selectedFloorInventoryId ?? scheduleFloorInventoryId;
  const startIso = schedule.start ?? schedule.startDate;
  const endIso = schedule.end ?? schedule.endDate;

  const availabilityArgs = useMemo(
    () => buildChangeRoomFloorAvailabilityArgs(schedule),
    [schedule]
  );

  const availabilityQuery = useQuery(
    ["changeConferenceRoomFloorAvailability", schedule.id, availabilityArgs],
    async () => {
      if (!availabilityArgs) {
        return [];
      }
      const { data } = await getFloorAvailability(availabilityArgs);
      return data;
    },
    { enabled: enabled && availabilityArgs != null }
  );

  const floorInventory = availabilityQuery.data ?? [];

  const currentFloorIndex = useMemo(() => {
    if (!floorInventory.length) {
      return 0;
    }
    const index = floorInventory.findIndex((f: FloorAvailability) => f.id === floorInventoryId);
    return index >= 0 ? index : 0;
  }, [floorInventory, floorInventoryId]);

  const currentFloor = useMemo(() => {
    if (!floorInventory.length) {
      return undefined;
    }
    return (
      floorInventory.find((f: FloorAvailability) => f.id === floorInventoryId) ?? floorInventory[0]
    );
  }, [floorInventory, floorInventoryId]);

  const floorPlanQuery = useRemoteFetchFloorPlan(
    floorInventoryId,
    startIso,
    endIso,
    enabled && floorInventoryId > 0 && Boolean(startIso && endIso)
  );

  return {
    floorInventoryId,
    scheduleFloorInventoryId,
    floorInventory,
    currentFloorIndex,
    currentFloor,
    floorPlan: floorPlanQuery.data,
    isLoading: availabilityQuery.isLoading || floorPlanQuery.isLoading,
    isError: availabilityQuery.isError || floorPlanQuery.isError,
    refetch: () => {
      availabilityQuery.refetch();
      floorPlanQuery.refetch();
    }
  };
}
